import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { listOrganizations, deleteOrganization } from '../api';
import { useAuth } from '../context/AuthContext';
import SearchInput from '../components/SearchInput';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import EditOrgModal from '../components/EditOrgModal';
import PortalIcon from '../components/PortalIcon';
import './styles.css';
import { Svg, initialsOf, useSort, sortBy, SortHead } from '../components/PortalUI';
import '../components/PortalUI.css';
import LoadingState from '../components/LoadingState';

export default function Organizations() {
  const navigate = useNavigate();
  const { permissions, isOwner } = useAuth();
  const canDelete = isOwner || permissions.includes('delete');
  // "Opret organisation" lives inline in this page's own heading row
  // below — the open/close state it drives is lifted to AdminLayout so the
  // modal survives regardless of which child route is mounted, and handed
  // down here via outlet context.
  const { addOrgOpen: createOpen, setAddOrgOpen: setCreateOpen } = useOutletContext();
  const [orgs, setOrgs] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [copiedId, setCopiedId] = useState('');
  const showLoading = !orgs && !error;

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const data = await listOrganizations();
      if (!Array.isArray(data.organizations)) throw new Error(data.error || 'Data ikke tilgængelig');
      setOrgs(data.organizations);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete() {
    const result = await deleteOrganization(deleteConfirm.id);
    if (!result.success) throw new Error(result.error || 'Kunne ikke slette organisationen');
    setDeleteConfirm(null);
    await load();
  }

  const filtered = useMemo(() => {
    if (!orgs) return [];
    if (!search.trim()) return orgs;
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => o.name.toLowerCase().includes(q));
  }, [orgs, search]);

  const [sort, toggleSort] = useSort('name');
  const sorted = useMemo(() => sortBy(filtered, sort, { name: (o) => o.name, count: (o) => o.tenantCount, contact: (o) => o.contactName, cvr: (o) => o.cvr }), [filtered, sort]);

  function openOrg(o) {
    navigate(`/tenants?org=${o.id}&orgName=${encodeURIComponent(o.name)}`);
  }

  function copyOrgId(id) {
    navigator.clipboard?.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 1500);
    });
  }

  return (
    <>
      <div className="pu-page">
        <div className="pu-head">
          <div>
            <h1>Organisationer</h1>
            <p>MSP'er og kunder, der drifter Microsoft 365-tenants gennem NordComply.</p>
          </div>
          <button type="button" className="pu-btn pu-btn--p" onClick={() => setCreateOpen(true)}><Svg name="building" />Opret organisation</button>
        </div>

        {error && <p className="state-error">{error}</p>}
        {showLoading && <LoadingState />}

        {!showLoading && orgs && (
          orgs.length === 0 ? (
            <div className="pu-empty">
              <p>Ingen organisationer endnu</p>
              <button type="button" onClick={() => setCreateOpen(true)} className="pu-btn pu-btn--p">Opret organisation</button>
            </div>
          ) : (
            <>
              <div className="pu-bar"><SearchInput value={search} onChange={setSearch} placeholder="Søg efter organisation..." /></div>
              {sorted.length === 0 ? (
                <div className="pu-empty"><p>Ingen organisationer matcher din søgning</p></div>
              ) : (
                <div className="pu-table">
                  <div className="pu-th pu-tr pu-tr--o"><SortHead label="Organisation" k="name" sort={sort} onSort={toggleSort} /><SortHead label="Kunder" k="count" sort={sort} onSort={toggleSort} /><SortHead label="Kontaktperson" k="contact" sort={sort} onSort={toggleSort} /><SortHead label="CVR" k="cvr" sort={sort} onSort={toggleSort} /><span /></div>
                  {sorted.map((o) => (
                    <div
                      key={o.id}
                      className="pu-tr pu-tr--o pu-tr--click"
                      role="button"
                      tabIndex={0}
                      onClick={() => openOrg(o)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openOrg(o); }}
                    >
                      <span className="pu-who">
                        <span className="pu-av">{initialsOf(o.name)}</span>
                        <span><b>{o.name}</b><small>{o.id}</small></span>
                      </span>
                      <span className="pu-c">{o.tenantCount} {o.tenantCount === 1 ? 'kunde' : 'kunder'}</span>
                      <span className="pu-who"><span><b style={{ fontWeight: 500 }}>{o.contactName || '—'}</b><small>{o.contactEmail || ''}</small></span></span>
                      <span className="pu-c pu-c--m">{o.cvr || '—'}</span>
                      <span className="pu-acts">
                        <button type="button" className="pu-ib" title={copiedId === o.id ? 'Kopieret' : 'Kopiér organisations-ID'} aria-label="Kopiér organisations-ID" onClick={(e) => { e.stopPropagation(); copyOrgId(o.id); }}>
                          <PortalIcon name={copiedId === o.id ? 'check' : 'copy'} />
                        </button>
                        <button type="button" className="pu-ib" title="Redigér organisation" aria-label="Redigér organisation" onClick={(e) => { e.stopPropagation(); setEditingOrg(o); }}><PortalIcon name="edit" /></button>
                        {canDelete && <button type="button" className="pu-ib pu-ib--del" title="Slet organisation" aria-label="Slet organisation" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(o); }}><PortalIcon name="trash" /></button>}
                        <span className="pu-go"><Svg name="chevron" size={15} /></span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="pu-foot">Klik på en organisation for at se dens kunder.</p>
            </>
          )
        )}
      </div>

      {createOpen && (
        <EditOrgModal create onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />
      )}

      {editingOrg && (
        <EditOrgModal
          org={editingOrg}
          onClose={() => setEditingOrg(null)}
          onSaved={() => { setEditingOrg(null); load(); }}
        />
      )}

      {deleteConfirm && (
        <ConfirmDeleteModal
          title="Slet organisation?"
          itemName={deleteConfirm.name}
          warning={`Dette sletter "${deleteConfirm.name}" permanent. Organisationen skal være tom for tenants og brugere. Handlingen kan ikke fortrydes.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </>
  );
}
